import {
    createRouter, createWebHistory,
} from 'vue-router';
import {
    redirect,
} from '../utils/navigation';
import profileExists from './middleware/profile-exists';
import fetchProfile from './middleware/fetch-profile';
import {
    CATEGORIES,
    trackPageView,
    trackUser,
    trackSubContentGroup,
} from '../utils/tracking';
import {
    log,
} from '../utils/action-logger';
import {
    isLoggedInUser,
} from './middleware/auth';
import {
    backendRoute,
} from '../utils/route';
import {
    useLoadingStore,
} from '../stores/loading-store';

/* eslint-disable max-len */
const PageApplications = () => import('../components/pages/PageApplications');
const PageApplicationForm = () => import('../components/pages/PageApplicationForm');
const PageProfileCreateTemplate = () => import('../components/pages/PageProfileCreateTemplate');
const FormProfileCreateOnePage = () => import('../components/organisms/form/profile-create/FormProfileCreateOnePage');
const PageProfileCreateOnePageWork = () => import('../components/pages/PageProfileCreateOnePageWork');
const PageProfileSave = () => import('../components/pages/PageProfileSave');
const PageConversations = () => import('../components/pages/PageConversations');
const PageCv = () => import('../components/pages/PageCv');
const PageCvDesiredJobForm = () => import('../components/pages/PageCvDesiredJobForm');
const PageCvEditOverview = () => import('../components/pages/PageCvEditOverview');
const PageCvSkillForm = () => import('../components/pages/PageCvSkillForm');
const PageCvFileEdit = () => import('../components/pages/PageCvFileEdit');
const PageCvFilePreview = () => import('../components/pages/PageCvFilePreview');
const PageDashboard = () => import('../components/pages/PageDashboard');
const PageError = () => import('../components/pages/PageError');
const PageMotivationLetters = () => import('../components/pages/PageMotivationLetters');
const PageMotivationLetterDetail = () => import('../components/pages/PageMotivationLetterDetail');
const PageMotivationLetterForm = () => import('../components/pages/PageMotivationLetterForm');
const PageNotFound = () => import('../components/pages/PageNotFound');
const PageProfileViews = () => import('../components/pages/PageProfileViews');
const PageVisibilityForm = () => import('../components/pages/PageVisibilityForm');
const SliderCvTemplates = () => import('../components/organisms/slider/SliderCvTemplates');
const AppConversationDetail = () => import('../components/organisms/app/AppConversationDetail');
const AppApplicationDetail = () => import('../components/organisms/app/AppApplicationDetail');
/* eslint-enable */

const routes = [
    {
        path: '/profil/anlegen',
        name: 'page-profile-create-onepage',
        component: PageProfileCreateOnePageWork,
        meta: {
            middleware: [fetchProfile],
        },
    },
    {
        path: '/profil/anlegen/vorlage',
        component: PageProfileCreateTemplate,
        meta: {
            middleware: [fetchProfile],
        },
        children: [
            {
                path: '',
                name: 'slider-cv-templates',
                component: SliderCvTemplates,
                props: route => ({
                    querySettings: {
                        template: route.query.template || null,
                        color: route.query.color || null,
                        font: route.query.font || null,
                    },
                    profileCreate: true,
                }),
            },
            {
                path: 'daten',
                name: 'form-profile-create-onepage',
                component: FormProfileCreateOnePage,
                meta: {
                    middleware: [fetchProfile],
                },
                props: {
                    fromTemplate: true,
                },
            },
        ],
    },
    {
        path: '/profil/anlegen/speichern',
        name: 'page-profile-save',
        component: PageProfileSave,
    },
    {
        path: '/profil/lebenslauf',
        name: 'page-cv',
        component: PageCv,
        meta: {
            middleware: [profileExists],
            trackingCategory: CATEGORIES.page.profile,
            subContentGroup: 'List View',
        },
    },
    {
        path: '/dashboard',
        name: 'page-dashboard',
        component: PageDashboard,
        meta: {
            trackingCategory: CATEGORIES.page.dashboard,
            middleware: [fetchProfile],
        },
    },
    {
        path: '/profil',
        beforeEnter: () => {
            redirect('/lebenslauf');
        },
    },
    {
        path: '/fehler',
        component: PageError,
    },
    {
        path: backendRoute('backend.authentication.oauthLogin'),
        name: 'login',
        beforeEnter: (to, from) => {
            useLoadingStore().initialLoadingOverlay = true;

            const loginUrl = new URL(to.fullPath, window.location.origin);

            // set redirect url as absolute url, either from query or the page you came from
            const relativeRedirectUrl = to.query.redirectUrl
                || (to.query.type === 'cv-created' && '/profil/lebenslauf?first-activation=karl')
                || to.redirectedFrom?.fullPath
                || from.fullPath;
            const absoluteRedirectUrl = new URL(relativeRedirectUrl, window.location.origin).href;
            loginUrl.searchParams.set('redirectUrl', absoluteRedirectUrl);

            redirect(loginUrl.href);
            return false;
        },
    },
    {
        path: backendRoute('backend.authentication.oauthLogout'),
        name: 'logout',
        beforeEnter: (to) => {
            useLoadingStore().initialLoadingOverlay = true;

            redirect(new URL(to.path, window.location.origin).href);
            return false;
        },
    },
    // deprecated legacy register path, in case some mail templates or external links try to link there
    {
        path: '/profil/registrieren',
        redirect: { name: 'register' },
    },
    {
        path: backendRoute('backend.authentication.oauthRegister'),
        name: 'register',
        beforeEnter: (to) => {
            const registerUrl = new URL(to.fullPath, window.location.origin);

            // set redirect url as absolute url, either from query or just redirect to dashboard
            const relativeRedirectUrl = to.query.redirectUrl
                || (to.query.type === 'cv-created' && '/profil/lebenslauf?first-activation=karl')
                || to.redirectedFrom?.fullPath
                || '/profil/dashboard';
            const absoluteRedirectUrl = new URL(relativeRedirectUrl, window.location.origin).href;
            registerUrl.searchParams.set('redirectUrl', absoluteRedirectUrl);

            redirect(registerUrl.href);
            return false;
        },
    },
    {
        path: '/profil/aufrufe',
        name: 'page-views',
        component: PageProfileViews,
        meta: {
            middleware: [profileExists],
        },
    },
    {
        path: '/profil/nachrichten',
        name: 'page-conversations',
        component: PageConversations,
        meta: {
            middleware: [profileExists],
            subContentGroup: 'Listing',
        },
        props: route => ({
            id: route.params.id,
            dirty: !!route.params.id,
        }),
        children: [{
            path: '',
            name: 'page-conversations',
            component: AppConversationDetail,
            meta: {
                middleware: [profileExists],
                subContentGroup: 'Listing',
            },
        }, {
            path: ':id',
            name: 'page-conversation-detail',
            component: AppConversationDetail,
            meta: {
                middleware: [profileExists],
            },
        }],
    },
    {
        path: '/profil/bewerbungen',
        component: PageApplications,
        meta: {
            middleware: [fetchProfile],
            trackingCategory: CATEGORIES.page.applications,
            subContentGroup: 'Listing',
        },
        props: route => ({
            id: route.params.id,
            subNavId: route.params.subNavId,
            dirty: !!route.params.id,
        }),
        children: [{
            path: '',
            name: 'page-applications',
            component: AppApplicationDetail,
            meta: {
                middleware: [fetchProfile],
                subContentGroup: 'Listing',
            },
        }, {
            path: ':id/:subNavId?',
            name: 'page-application-detail',
            component: AppApplicationDetail,
            meta: {
                middleware: [fetchProfile],
            },
        }],
    },
    {
        path: '/profil/bewerbungen/anlegen',
        name: 'page-application-create',
        component: PageApplicationForm,
        meta: {
            subContentGroup: 'Detail',
        },
    },
    {
        path: '/profil/bewerbungen/:id/bearbeiten',
        props: true,
        name: 'page-application-edit',
        component: PageApplicationForm,
        meta: {
            trackingCategory: CATEGORIES.page.applications,
            subContentGroup: 'Detail',
        },
    },
    // Bewerbungsschreiben are disabled, until the feature is revamped
    {
        path: '/profil/bewerbungsschreiben',
        name: 'page-motivation-letters',
        component: PageMotivationLetters,
        meta: {
            middleware: [profileExists],
        },
        redirect: '/dashboard',
    },
    {
        path: '/profil/bewerbungsschreiben/anlegen',
        name: 'page-motivation-letter-create',
        component: PageMotivationLetterForm,
        redirect: '/dashboard',
    },
    {
        path: '/profil/bewerbungsschreiben/:id',
        props: true,
        name: 'page-motivation-letter-detail',
        component: PageMotivationLetterDetail,
        meta: {
            middleware: [fetchProfile],
        },
        redirect: '/dashboard',
    },
    {
        path: '/profil/bewerbungsschreiben/:id/bearbeiten',
        props: true,
        name: 'page-motivation-letter-edit',
        component: PageMotivationLetterForm,
        redirect: '/dashboard',
    },
    {
        path: backendRoute('backend.cvFile.preview'),
        name: 'pdf',
        meta: {
            middleware: [profileExists],
            subContentGroup: 'Preview',
        },
        beforeEnter: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: backendRoute('profileImage.get'),
        name: 'profile-image',
        meta: {
            middleware: [profileExists],
        },
        beforeEnter: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: '/lebenslauf',
        name: 'cv-landing-page',
        meta: {
            // If an error occurs while fetching the profile, an error-popup is shown
            // This setting will persist the popup when the route changes.
            popup: 'persistOnRouteChange',
        },
        beforeEnter: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: '/companyalarm/:pathMatch(.*)',
        name: 'page-company-alarm-detail',
        beforeEnter: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: '/f/:pathMatch(.*)',
        redirect: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: '/b/:pathMatch(.*)',
        redirect: (to) => {
            redirect(to.fullPath);
        },
    },
    {
        path: '/profil/lebenslauf/wunschjob',
        name: 'page-cv-desired-job-form',
        component: PageCvDesiredJobForm,
        props: route => ({ target: route.params.target, matching: route.query.matching }),
        meta: {
            middleware: [profileExists],
        },
        beforeEnter: (to, from, next) => {
            // Only show this if "matching" param is given in the URL
            // Currently only done in k4 on /matching to open this page in a modal
            if (to.query.matching) {
                next();
            } else {
                redirect('/dashboard');
            }
        },
    },
    {
        path: '/profil/lebenslauf/kenntnisse',
        name: 'page-cv-skill-form',
        component: PageCvSkillForm,
        props: route => ({ matching: route.query.matching }),
        meta: {
            middleware: [profileExists],
        },
        beforeEnter: (to, from, next) => {
            // Only show this if "matching" param is given in the URL
            // Currently only done in k4 on /matching to open this page in a modal
            if (to.query.matching) {
                next();
            } else {
                redirect('/dashboard');
            }
        },
    },
    {
        path: '/profil/sichtbarkeit',
        name: 'page-visibility-form',
        component: PageVisibilityForm,
        props: { standalone: true },
        meta: {
            middleware: [profileExists],
        },
    },
    {
        path: '/profil/lebenslauf/design-aendern',
        name: 'page-cv-file-edit',
        component: PageCvFileEdit,
        props: route => ({ reset: Boolean(route.query.reset) }),
        meta: {
            middleware: [profileExists],
            subContentGroup: 'Change Template',
        },
    },
    {
        path: '/profil/lebenslauf/design-vorschau/',
        name: 'page-cv-file-preview',
        component: PageCvFilePreview,
        meta: {
            middleware: [profileExists],
        },
    },
    {
        path: '/profil/lebenslauf/bearbeitungs-uebersicht',
        name: 'page-cv-edit-overview',
        component: PageCvEditOverview,
        meta: {
            middleware: [profileExists],
        },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: PageNotFound,
    },
];

const router = createRouter({
    routes,
    history: createWebHistory(),
    linkActiveClass: 'is-active',
    linkExactActiveClass: 'is-exact-active',
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }

        return { left: 0, top: 0 };
    },
});

// Prevent debug page to be available on production and staging (same build).
if (process.env.NODE_ENV === 'development') {
    router.addRoute({
        path: '/profil/debug',
        name: 'debug',
        component: () => import('../components/pages/PageDebug'),
    });
}

// Run through all supplied middleware
router.beforeEach(async (to, from) => {
    const loading = useLoadingStore();

    if (to.name !== from.name && !loading.initialLoadingOverlay) loading.startProgressBar();

    log(to.name).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    });

    // eslint-disable-next-line no-param-reassign
    to.meta.referrer = from;

    let middleware = [];
    if (to.meta.middleware) {
        middleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware];
    }
    // Always check if user is logged in
    middleware.unshift(isLoggedInUser);

    const context = {
        from,
        router,
        to,
    };

    loading.initialLoadingOverlay = false;

    // Loop through middleware, return result if navigation failed middleware validation
    for (const nextMiddlewareInLine of middleware) {
        // eslint-disable-next-line no-await-in-loop
        const middlewareResult = await nextMiddlewareInLine(context);
        if (middlewareResult === false || typeof middlewareResult === 'object') {
            return middlewareResult;
        }
    }

    return true;
});

router.afterEach((to, from) => {
    const loading = useLoadingStore();

    if (to.name !== from.name) loading.finishProgressBar();
    trackUser();

    if (to.meta?.subContentGroup) trackSubContentGroup(to.meta?.subContentGroup);

    const isNotInitialPageview = from.name !== null;
    // Don't track initial pageview because it is tracked automatically.
    if (isNotInitialPageview) {
        trackPageView(to.fullPath);
    }
});

export default router;
